/**
 * Maps the connector live sample (`GET /api/v1/connectors/{slug}/assets/{id}/sample`) into the
 * same shapes the v2 columns and rows endpoints are mapped to, so the Columns and Preview views
 * render a bridged source (sheets, SaaS objects) without knowing where the data came from.
 */
import type { ConnectorAssetSampleResponsePayload } from "../api";
import { mapConnectorColumn } from "./dataStudioColumnProfileMappers";
import type {
  DataStudioProfileCellValue,
  DataStudioProfileRow,
  GetDataStudioTableColumnsResponse,
  GetDataStudioTableRowsResponse,
} from "./dataStudioTables.types";

/** The sample endpoint serves at most this many rows per page. */
export const LIVE_SAMPLE_MAX_ROWS = 50;

/** Asset types whose columns and rows only exist as a live sample (no stored column discovery). */
export const LIVE_SAMPLE_ASSET_TYPES: readonly string[] = ["sheet", "saas_object"];

export const usesLiveSample = (assetType: string | null | undefined): boolean =>
  Boolean(assetType && LIVE_SAMPLE_ASSET_TYPES.includes(assetType));

/** The sample carries no column types, so read one off the values seen. */
const inferDataType = (values: unknown[]): string => {
  const seen = values.filter((value) => value !== null && value !== undefined && value !== "");
  if (seen.length === 0) return "varchar";
  if (seen.every((value) => typeof value === "boolean")) return "boolean";
  if (seen.every((value) => typeof value === "number")) {
    return seen.every((value) => Number.isInteger(value)) ? "integer" : "double";
  }
  return "varchar";
};

const toCell = (cell: unknown): DataStudioProfileCellValue => {
  if (cell === null || cell === undefined) return null;
  if (typeof cell === "number" || typeof cell === "string") return cell;
  if (typeof cell === "boolean") return String(cell);
  return JSON.stringify(cell);
};

/** A sample column is identified by its name — that is all the sample sends. */
const columnId = (name: string): string => name;

export const mapLiveSampleColumns = (
  sample: ConnectorAssetSampleResponsePayload,
): GetDataStudioTableColumnsResponse => ({
  columns: sample.columns.map((name, index) =>
    mapConnectorColumn({
      id: columnId(name),
      name,
      data_type: inferDataType(sample.rows.map((row) => row[index])),
      is_nullable: true,
      is_primary_key: false,
      is_foreign_key: false,
      ordinal_position: index + 1,
      data_quality_score: null,
    }),
  ),
  columnPageInfo: {
    total: sample.columns.length,
    nextOffset: null,
    hasMore: false,
    previousOffset: null,
    hasPrevious: false,
  },
});

/** `offset`/`limit` are the row window the caller asked for; the sample itself pages by page number. */
export const mapLiveSampleRows = (
  sample: ConnectorAssetSampleResponsePayload,
  offset = 0,
  limit = sample.limit_rows,
): GetDataStudioTableRowsResponse => {
  const hasMore = sample.next_page !== null && sample.next_page !== undefined;
  const rows: DataStudioProfileRow[] = sample.rows.map((tuple, rowIndex) => {
    const values: DataStudioProfileRow["values"] = {};
    sample.columns.forEach((name, index) => {
      values[columnId(name)] = toCell(tuple[index]);
    });
    return { id: `row-${offset + rowIndex}`, values };
  });
  return {
    rows,
    rowPageInfo: {
      total: offset + rows.length + (hasMore ? 1 : 0),
      nextOffset: hasMore ? offset + limit : null,
      hasMore,
      previousOffset: offset > 0 ? Math.max(0, offset - limit) : null,
      hasPrevious: offset > 0,
    },
    columnOrder: sample.columns.map((name, index) => ({
      id: columnId(name),
      name,
      dataType: inferDataType(sample.rows.map((row) => row[index])),
    })),
  };
};
